'use client';

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { userService } from '../api/user/userApi';
import { isSessionHandledError } from '@/lib/api';
import { SubscriptionRole } from '@/types';

// Roles operativos del panel: quien tiene alguno ya opera en GetMatch. Espeja
// STAFF_ROLES del backend (UserService.kt) sin FUTURE_OWNER, que es el rol de
// quien todavía no opera.
const OPERATIONAL_ROLES: string[] = [
  SubscriptionRole.OWNER,
  SubscriptionRole.MANAGER,
  SubscriptionRole.INSTRUCTOR,
];

const hasOperationalRole = (roles?: string[]): boolean =>
  roles?.some((role) => OPERATIONAL_ROLES.includes(role)) ?? false;

/**
 * FUTURE_OWNER: entró al manager pero todavía no tiene club aprobado, así que
 * solo puede acceder a /clubs y /cuenta-corriente para dar de alta el primero.
 *
 * No aplica si ya tiene un rol operativo: un profesor invitado conserva
 * FUTURE_OWNER junto con INSTRUCTOR y tiene que entrar al panel como profesor.
 *
 * Pura sobre los roles para que el login pueda decidir el destino con los datos
 * recién traídos, antes de que el hook vea el usuario en el store.
 */
export const isFutureOwnerByRoles = (roles?: string[]): boolean =>
  (roles?.includes(SubscriptionRole.FUTURE_OWNER) ?? false) &&
  !hasOperationalRole(roles);

export const useAuth = () => {
  const { isAuthenticated, clearTokens, user, isLoadingUser } = useAuthStore(); // Get isLoadingUser
  const navigate = useNavigate();
  const authenticated = isAuthenticated();

  // State to track if user data fetch has been initiated for the current session
  // This helps prevent multiple fetches if the hook runs multiple times before user is set.
  const [hasFetchedUser, setHasFetchedUser] = useState(false);

  const hasOwnerRole = useMemo(() => {
    return user?.roles?.includes(SubscriptionRole.OWNER) ?? false;
  }, [user]);

  const hasManagerRole = useMemo(() => {
    return user?.roles?.includes(SubscriptionRole.MANAGER) ?? false;
  }, [user]);

  const hasInstructorRole = useMemo(() => {
    return user?.roles?.includes(SubscriptionRole.INSTRUCTOR) ?? false;
  }, [user]);

  const hasAdminRole = useMemo(() => {
    return user?.roles?.includes(SubscriptionRole.ADMIN) ?? false;
  }, [user]);

  const hasPendingDebtRole = useMemo(() => {
    return user?.roles?.includes(SubscriptionRole.PENDING_DEBT) ?? false;
  }, [user]);

  // Instructor "puro": tiene rol INSTRUCTOR pero no es owner ni manager.
  // Solo a este perfil se le recorta el panel (ver Sidebar y RequireOwnerOrManager).
  const isPureInstructor = useMemo(() => {
    return hasInstructorRole && !hasOwnerRole && !hasManagerRole;
  }, [hasInstructorRole, hasOwnerRole, hasManagerRole]);

  // Perfil "PENDING_DEBT puro": tiene la deuda pendiente pero ningún rol operativo.
  // Solo puede ver y saldar su cuenta corriente (ver Sidebar y RequirePanelAccess).
  const isPendingDebtOnly = useMemo(() => {
    return hasPendingDebtRole && !hasOperationalRole(user?.roles);
  }, [hasPendingDebtRole, user]);

  const isFutureOwner = useMemo(() => {
    return isFutureOwnerByRoles(user?.roles);
  }, [user]);

  // Pure ADMIN: internal GetMatch staff with no club of their own. The sidebar is
  // trimmed down to the backoffice section, same as isPureInstructor / isPendingDebtOnly.
  const isPureAdmin = useMemo(() => {
    return (
      hasAdminRole && !hasOwnerRole && !hasManagerRole && !hasInstructorRole
    );
  }, [hasAdminRole, hasOwnerRole, hasManagerRole, hasInstructorRole]);

  // Every flag above reads user.roles, and `user` is null until getCurrentUser
  // resolves — the store persists tokens only, not the profile. `isLoadingUser`
  // is NOT enough to wait on: it starts false, so on the first render after a
  // reload every flag is false and a guard that trusts them takes the wrong
  // branch. Anything that decides on a role must wait for this.
  const isUserReady = !isLoadingUser && !!user;

  useEffect(() => {
    // Subscribe to the store's hydration status
    const unsubscribe = useAuthStore.persist.onHydrate(() => {
      // No specific action needed here, just ensures hasHydrated() is reliable
    });

    const checkAuthAndFetchUser = async () => {
      if (!authenticated) {
        navigate('/login');
        return;
      }

      // If user is not in the store AND not currently loading
      // AND the store has finished hydrating (to ensure tokens are loaded)
      if (!user && !isLoadingUser && useAuthStore.persist.hasHydrated()) {
        setHasFetchedUser(true); // Mark that we are attempting to fetch
        try {
          await userService.getCurrentUser(); // This will initiate the request if none is in progress
        } catch (error) {
          // The interceptor already settled this one at the session level and
          // left a valid session behind — an expired support session whose
          // admin tokens it restored, or a session replaced while this request
          // was in flight. Logging out here would throw away the session it
          // just recovered and bounce the admin to /login, which is precisely
          // what that branch exists to prevent. The effect re-runs on the
          // restored session and fetches that user instead.
          if (isSessionHandledError(error)) {
            return;
          }
          console.error('Failed to fetch user data on auth hook:', error);
          clearTokens(); // Clear tokens if user data fetch fails
          navigate('/login');
        }
      }
    };

    checkAuthAndFetchUser();

    // Clean up the hydration listener when the component unmounts
    return () => {
      unsubscribe();
    };
  }, [
    authenticated,
    navigate,
    user,
    isLoadingUser,
    clearTokens,
    hasFetchedUser,
  ]); // Add isLoadingUser to dependencies

  return {
    isAuthenticated: authenticated,
    clearTokens,
    user,
    isLoadingUser,
    isUserReady,
    hasOwnerRole,
    hasManagerRole,
    hasInstructorRole,
    hasAdminRole,
    isPureInstructor,
    isPendingDebtOnly,
    isFutureOwner,
    isPureAdmin,
  }; // Return isLoadingUser
};
